<template>
  <span class="teleport-tooltip" :class="rootClass">
    <span
      ref="triggerRef"
      class="teleport-tooltip-trigger"
      :class="triggerWrapperClass"
      tabindex="0"
      @mouseenter="onEnter"
      @mouseleave="onLeave"
      @focusin="onEnter"
      @focusout="onLeave"
    >
      <slot name="trigger">
        <span :class="triggerLabelClass">{{ label }}</span>
      </slot>
    </span>
    <Teleport to="body">
      <div
        v-show="visible"
        ref="tooltipRef"
        class="teleport-tooltip-popup"
        :style="popupStyle"
        role="tooltip"
      >
        <slot>{{ description }}</slot>
        <div class="teleport-tooltip-arrow" :style="arrowStyle" />
      </div>
    </Teleport>
  </span>
</template>

<script>
/**
 * Teleported hover/focus tooltip. Renders popup on document body so it is not
 * clipped by overflow, sticky headers, or nested stacking contexts.
 */
export default {
  name: 'TeleportTooltip',
  props: {
    label: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    /** When true, root and trigger span full width (e.g. metric bars). */
    block: {
      type: Boolean,
      default: false
    },
    /** dotted = underlined help label; plain = settings-style label text */
    variant: {
      type: String,
      default: 'dotted',
      validator: (v) => ['dotted', 'plain'].includes(v)
    },
    labelClass: {
      type: String,
      default: ''
    },
    /** Max width of the popup (CSS length). Default 20rem. */
    maxWidth: {
      type: String,
      default: '20rem'
    }
  },
  data() {
    return {
      visible: false,
      tooltipStyle: {},
      arrowStyle: {}
    }
  },
  computed: {
    rootClass() {
      return this.block ? 'block w-full' : 'inline-flex justify-center'
    },
    triggerWrapperClass() {
      return this.block ? 'block w-full' : 'inline-flex'
    },
    triggerLabelClass() {
      if (this.labelClass) return this.labelClass
      if (this.variant === 'plain') {
        return 'text-sm text-gray-600 cursor-help'
      }
      return 'cursor-help border-b border-dotted border-gray-400'
    },
    popupMaxWidth() {
      return `min(${this.maxWidth}, calc(100vw - 16px))`
    },
    popupStyle() {
      return {
        ...this.tooltipStyle,
        maxWidth: this.popupMaxWidth
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScrollOrResize, true)
    window.addEventListener('resize', this.onScrollOrResize)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScrollOrResize, true)
    window.removeEventListener('resize', this.onScrollOrResize)
  },
  methods: {
    onEnter() {
      this.visible = true
      this.$nextTick(() => this.updatePosition())
    },
    onLeave() {
      this.visible = false
    },
    onScrollOrResize() {
      if (this.visible) this.updatePosition()
    },
    updatePosition() {
      const trigger = this.$refs.triggerRef
      const tooltip = this.$refs.tooltipRef
      if (!trigger || !tooltip) return

      const rect = trigger.getBoundingClientRect()
      const tipRect = tooltip.getBoundingClientRect()
      const gap = 8
      const arrowSize = 4

      const top = rect.top - tipRect.height - gap
      const left = rect.left + rect.width / 2 - tipRect.width / 2
      const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - tipRect.width - 8))
      const arrowLeft = rect.left + rect.width / 2 - clampedLeft - arrowSize

      this.tooltipStyle = {
        top: `${Math.max(8, top)}px`,
        left: `${clampedLeft}px`
      }
      this.arrowStyle = {
        top: `${tipRect.height - arrowSize}px`,
        left: `${arrowLeft}px`,
        borderColor: 'transparent',
        borderTopColor: '#111827'
      }
    }
  }
}
</script>

<style scoped>
.teleport-tooltip-trigger {
  display: inline-flex;
  outline: none;
}

.teleport-tooltip-trigger.block {
  display: block;
}

.teleport-tooltip-popup {
  position: fixed;
  z-index: 9999;
  max-width: min(20rem, calc(100vw - 16px));
  padding: 0.5rem 0.75rem;
  background: #111827;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
  line-height: 1.25;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  pointer-events: none;
  white-space: normal;
}

.teleport-tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-width: 4px;
  border-style: solid;
}
</style>
